import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowLeft, Upload, FileText, CheckCircle2, Trash2, Download, AlertCircle } from "lucide-react";

/**
 * Solicitor-admin: upload master fillable TA6 / TA7 / TA10 / TA13 PDFs
 * (AcroForms) that clients on the onboarding wizard Step 5 will fill
 * in-browser, sign, and submit back to the case documents.
 *
 * Backend: `/api/firm/ta-templates`.
 */
export default function FirmTaTemplatesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const inputRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/firm/ta-templates");
      setRows(r.data.templates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load templates");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const chooseFile = (form) => inputRefs.current[form]?.click();

  const uploadFile = async (form, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please choose a PDF file.");
      return;
    }
    setUploading(form);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/firm/ta-templates/${form}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data.warning) toast.warning(r.data.warning);
      else toast.success(`${form} uploaded — ${r.data.field_count} fillable field(s) detected.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(null); }
  };

  const remove = async (form) => {
    if (!window.confirm(`Delete the ${form} template? Clients on new cases will not see this form until re-uploaded.`)) return;
    try {
      await api.delete(`/firm/ta-templates/${form}`);
      toast.success(`${form} template deleted.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const download = (form) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/firm/ta-templates/${form}/download`;
    window.open(url, "_blank");
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="firm-ta-templates-page">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate("/settings")}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6"
          data-testid="ta-templates-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to settings
        </button>
        <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          TA form templates
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Upload master fillable PDFs (AcroForm) for each TA form your firm uses.
          Clients on the onboarding wizard Step 5 will complete these directly
          in their browser, apply their e-signature, and the signed PDF will be
          filed into the case's Onboarding folder.
        </p>
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-800">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            Download blank TA forms with fillable fields from the
            {" "}<a className="underline" target="_blank" rel="noreferrer"
              href="https://www.lawsociety.org.uk/topics/property/transaction-forms">
              Law Society transaction forms</a>{" "}
            page. Make sure the PDF has form fields (AcroForm) — flat scans will
            not be editable.
          </div>
        </div>

        {loading ? (
          <p className="mt-8 text-sm text-slate-400">Loading…</p>
        ) : (
          <div className="mt-6 space-y-3">
            {rows.map(r => (
              <div key={r.form}
                className={`rounded-xl border p-5 flex items-center gap-4 ${r.uploaded ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
                data-testid={`ta-template-row-${r.form}`}>
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${r.uploaded ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                  {r.uploaded ? <CheckCircle2 className="w-6 h-6" /> : <FileText className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-900">{r.form}</h3>
                    <span className="text-xs text-slate-500">{FORM_LABELS[r.form]}</span>
                    {r.solicitor_only && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                        Solicitor only
                      </span>
                    )}
                  </div>
                  {r.uploaded ? (
                    <p className="text-xs text-slate-500 mt-1 truncate">
                      <span className="font-mono">{r.filename}</span> · {r.field_count || 0} fillable fields
                      {r.uploaded_at ? ` · uploaded ${new Date(r.uploaded_at).toLocaleString("en-GB")}` : ""}
                      {r.uploaded_by ? ` by ${r.uploaded_by}` : ""}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1">Not uploaded — clients on the wizard won't see this form.</p>
                  )}
                </div>
                <input ref={el => (inputRefs.current[r.form] = el)} type="file" accept="application/pdf"
                  onChange={(e) => uploadFile(r.form, e)} className="hidden"
                  data-testid={`ta-template-file-${r.form}`} />
                {r.uploaded && (
                  <button onClick={() => download(r.form)} title="Download current"
                    data-testid={`ta-template-download-${r.form}`}
                    className="p-2 rounded-lg text-slate-500 hover:text-[#100f5d] hover:bg-slate-100">
                    <Download className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => chooseFile(r.form)} disabled={uploading === r.form}
                  data-testid={`ta-template-upload-${r.form}`}
                  className="px-3 py-2 rounded-lg text-xs font-semibold bg-[#100f5d] text-white hover:bg-[#0b0a3d] disabled:opacity-50 inline-flex items-center gap-1.5">
                  <Upload className="w-3.5 h-3.5" />
                  {uploading === r.form ? "Uploading…" : r.uploaded ? "Replace" : "Upload"}
                </button>
                {r.uploaded && (
                  <button onClick={() => remove(r.form)} title="Delete"
                    data-testid={`ta-template-delete-${r.form}`}
                    className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const FORM_LABELS = {
  TA6:  "Property Information (freehold & leasehold)",
  TA7:  "Leasehold Information",
  TA10: "Fittings & Contents",
};
